import { useEffect, useState, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { debounce } from 'lodash'
import { Dropdown, SearchElement, type IDropdownProps } from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { SearchTypesEnum } from 'widgets/SearchDropdown'
import { TagsDropdownContent } from 'widgets/SearchDropdown/ui'
import { type SearchDropdownTagsStore } from 'widgets/SearchDropdown/store/searchDropdownTagsStore'
import styles from './styles.module.scss'

type TagElementProps = {
  parentsDropdownId?: string[]
  store: SearchDropdownTagsStore
  onRemove: (type: SearchTypesEnum) => void
  activeType: SearchTypesEnum | null
  canShowChildDropdown: boolean
  dropdownProps?: Partial<IDropdownProps>
}

export const TagElement: FC<TagElementProps> = observer(
  ({ parentsDropdownId, store, onRemove, activeType, canShowChildDropdown, dropdownProps }) => {
    const [show, setShow] = useState(false)
    const { tagsStore } = useEntitiesContext()
    const { hasSelectedItems, selectedItemsCount, firstSelectedItemId, disableClosing } = store

    const TagLabel = () => {
      const firstTag = firstSelectedItemId ? tagsStore.getItem(firstSelectedItemId) : null

      if (hasSelectedItems && selectedItemsCount === 1 && firstTag) {
        return (
          <div className={styles.countLabelWrapper}>
            <span className={styles.label}>{firstTag.label}</span>
          </div>
        )
      }

      return (
        <span className={styles.countLabelWrapper}>
          <span className={styles.countLabel}>{selectedItemsCount} tags</span>
        </span>
      )
    }

    const onShow = () => {
      const showDropdown = activeType === SearchTypesEnum.tags && canShowChildDropdown

      setShow(showDropdown)
    }

    const showDebounce = debounce(onShow, 300)

    useEffect(() => {
      showDebounce()
    }, [canShowChildDropdown, activeType])

    return (
      <Dropdown
        ariaLabel={'TagElement'}
        parentsDropdownId={parentsDropdownId}
        iconButtonProps={{
          size: 'medium',
          fontSize: 20,
          ariaLabel: 'TagElement_trigger',
        }}
        show={show}
        triggerComponent={(open) => (
          <SearchElement
            icon={'tag'}
            type='Tag'
            contentDefaultValue='select'
            warningContent={!open && !hasSelectedItems}
            removeCallback={() => {
              onRemove(SearchTypesEnum.tags)
            }}
            isViewOnly={disableClosing}
            content={hasSelectedItems && <TagLabel />}
          />
        )}
        placement={'bottom-start'}
        sizeTriggerButton={'medium'}
        variantTriggerButton={'icon'}
        customComponent={() => <TagsDropdownContent store={store} />}
        {...dropdownProps}
      />
    )
  }
)
